/** @format */

import { FacetVO, PlpState, ProductVO } from '@plp/data-access/interfaces';
import { createFeatureSelector, createSelector } from '@ngrx/store';

/**  @description Product State Selectors */
export const selectPLPAppState = createFeatureSelector<PlpState>('productList');

export const selectProductListCore = createSelector(
	selectPLPAppState,
	(state) => state?.productListCore
);

export const selectProductList = createSelector(
	selectProductListCore,
	(state) => state?.productList
);

export const selectProductListSEO = createSelector(
	selectProductListCore,
	(state) => state.productListSEO
);

export const selectFeatureFlags = createSelector(
	selectProductListCore,
	(state) => state.featureFlags
);

export const selectProductListSEOAttributes = createSelector(
	selectProductListSEO,
	(state) => state?.attr
);

export const selectProductListSEOName = createSelector(
	selectProductListSEO,
	(state) => state?.name
);

export const selectSEOBreadcrumbs = createSelector(
	selectProductListSEO,
	(state) => state?.breadCrumbDetails
);

export const selectIsAntiClearance = createSelector(
	selectProductListCore,
	(state) =>
		state?.productListSEO?.dsgClearance === false && state?.snbAudience === 'antiClearanceTest'
);

export const selectProductListFacetVOs = createSelector(
	selectProductList,
	(state) => state?.facetVOs
);

export const selectValidFacetVOs = createSelector(selectProductListFacetVOs, (facetVOs) => {
	if (!facetVOs?.length) {
		return [];
	}

	return facetVOs.filter((facetVO) => facetVO.valueList?.length);
});

export const selectHasCatBubbles = createSelector(selectValidFacetVOs, (facetVOs) => {
	return facetVOs.some((facet) => facet.attrIdentifier === 'facetCatgroup');
});

export const selectProductVOs = createSelector(
	selectProductList,
	(state) => state?.productVOs || []
);

export const selectProductListCategoryDetails = createSelector(
	selectProductList,
	(state) => state?.categoryDetails
);

export const selectFulfillmentFacets = createSelector(
	selectProductList,
	(state) => state?.fulfillmentFacets || []
);

export const selectFulfillmentFacetCounts = createSelector(selectFulfillmentFacets, (facets) => {
	return (
		facets?.reduce(
			(facetsMap, facet) => {
				facetsMap[facet.name] = facet.count;
				return facetsMap;
			},
			{} as Record<string, number>
		) ?? {}
	);
});

export const selectSkeletonLoader = createSelector(
	selectProductListCore,
	(state) => state.isSkeletonLoaderVisible
);

export const selectIsFilterSidePanelOpen = createSelector(
	selectProductListCore,
	(state) => state.isFilterSidePanelOpen
);

export const selectProductListAvailability = createSelector(
	selectProductListCore,
	(state) => state.productAvailability
);

export const selectProductListPromotion = createSelector(
	selectProductListCore,
	(state) => state?.productPromotion
);

export const selectSelectedSort = createSelector(
	selectProductListCore,
	(state) => state?.selectedSort
);

export const selectPageNumber = createSelector(selectProductListCore, (state) => state.pageNumber);

export const selectPageSize = createSelector(selectProductListCore, (state) => state.pageSize);

export const selectPIQProducts = createSelector(
	selectProductListCore,
	(state) => state.piqProducts
);

export const selectCriteoProducts = createSelector(
	selectProductListCore,
	(state) => state.criteoProducts
);

export const selectPinnedContent = createSelector(
	selectProductListCore,
	(state) => state.pinnedContent
);

export const selectProductDetails = createSelector(
	selectProductList,
	(state) => state?.productDetails
);

export const selectHomrBannerData = createSelector(
	selectProductListCore,
	(state) => state.homrBannerData
);

export const selectIsProductListPageLoading = createSelector(
	selectProductListCore,
	(state) => state.isProductListPageLoading
);

export const selectIsProductListPageValid = createSelector(
	selectProductListCore,
	(state) => state.isProductListPageValid
);

export const selectIsInitialPageLoad = createSelector(
	selectProductListCore,
	(state) => state.isInitialPageLoad
);

export const selectSearchVO = createSelector(selectProductList, (state) => state?.searchVO);

export const selectTotalCount = createSelector(selectSearchVO, (state) => state?.totalCount);

export const selectSelectedFilters = createSelector(
	selectProductListCore,
	(state) => state.selectedFilters
);

export const selectSelectedFacetsWithCount = createSelector(
	selectSelectedFilters,
	selectProductListFacetVOs,
	(selectedFilters, facetVOs) =>
		Object.keys(selectedFilters).map((key) => ({
			facet: key,
			values: selectedFilters[key].map((value) => {
				let count = 0;
				const filteredFacets: FacetVO = facetVOs.find(
					(facet) => facet.attrIdentifier === key
				);

				filteredFacets?.valueList.forEach((facetValue) => {
					if (facetValue.value === value) {
						count = facetValue.count;
					}
				});

				return { count, value };
			})
		}))
);

export const selectMlBypass = createSelector(selectProductListCore, (state) => state.mlBypass);

export const selectSnbAudience = createSelector(
	selectProductListCore,
	(state) => state.snbAudience
);

export const selectfacetCatgroupValueList = createSelector(
	selectProductListFacetVOs,
	(facetVOs: FacetVO[] = []) => {
		if (!facetVOs?.length) {
			return [];
		}

		return facetVOs.map((facet) => {
			if (facet.attrIdentifier === 'facetCatgroup') {
				return facet.valueList;
			}
		});
	}
);

export const selectProductECodeList = createSelector(
	selectProductVOs,
	(productVOs: ProductVO[]): string[] => {
		if (!productVOs?.length) {
			return [];
		}
		return productVOs.map((product) => product.parentPartnumber);
	}
);

export const selectRefinementUpdateObj = createSelector(
	selectProductListCore,
	(state) => state.refinementUpdateObj
);

export const selectCustomBadges = createSelector(
	selectProductListCore,
	(state) => state?.customBadges
);

export const selectDisplayableProductListData = createSelector(
	selectProductListCore,
	(state) => state?.displayableProductListData
);

export const selectFallbackSearchParams = createSelector(
	selectProductListCore,
	(state) => state.fallbackSearchParams
);
